import {InfiniteData} from '@tanstack/query-core';
import {
  infiniteQueryOptions,
  queryOptions,
  UseInfiniteQueryOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import {INVEN_QUERY_KEY_MAP} from '../api/inven/constant';
import {getInvenLike} from '../api/inven/getInvenLike';
import {getInvenMyInfo} from '../api/inven/getInvenMyInfo';
import {getInvenPaid} from '../api/inven/getInvenPaid';
import {getInvenRecent} from '../api/inven/getInvenRecent';
import {getInvenRecommend} from '../api/inven/getInvenRecommend';
import {postInvenSearch} from '../api/inven/postInvenSearch';
import {InvenMyInfo} from '../api/type.client';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {InvenTypeVo} from '../common/shared/api/gateway/enums/InvenTypeVo.enum';
import {PagewebError} from '../errors/PagewebError.class';
import {createSimpleApiResponseError} from '../graphql/api/createSimpleApiResponseError';
import {createInvenList} from '../graphql/factory/InvenList';
import {createInvenMyInfo} from '../graphql/factory/InvenMyInfo';
import {createInvenRecommedList} from '../graphql/factory/invenRecommendList';
import {
  InvenLikeListQuery,
  InvenLikeListQueryVariables,
  InvenMyInfoQueryVariables,
  InvenPaidListQuery,
  InvenPaidListQueryVariables,
  InvenRecentListQuery,
  InvenRecentListQueryVariables,
  InvenRecommendList,
  InvenRecommendListQueryVariables,
  SearchInvenListQuery,
  SearchInvenListQueryVariables,
} from '../graphql/generated/types';

export const invenFactory = {
  /**
   * 찜 목록 조회 (Infinite)
   *
   * ⚠️ Resolver 로직:
   * - response.result 없으면 에러 throw
   * - createInvenList factory로 변환 (InvenTypeVo.Like, userInfo 전달)
   *
   * ⚠️ Adapter에서 initialPageParam과 getNextPageParam을 반드시 전달해야 함
   */
  infiniteInvenLikeList: (
    variables: InvenLikeListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        InvenLikeListQuery, // TQueryFnData - GraphQL 응답 타입으로 변경
        PagewebError,
        InfiniteData<InvenLikeListQuery, InvenLikeListQueryVariables>,
        InvenLikeListQuery, // TResult
        ReturnType<typeof INVEN_QUERY_KEY_MAP.infiniteInvenLikeList>,
        InvenLikeListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: INVEN_QUERY_KEY_MAP.infiniteInvenLikeList(variables),
      queryFn: async ({pageParam}) => {
        const {categoryUid, sortType, size} = variables.invenListInput;

        const response = await getInvenLike(
          {
            category_uid: categoryUid ?? undefined,
            sort_type: sortType ?? undefined,
            page: pageParam?.invenListInput?.page ?? undefined,
            size: size ?? undefined,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError(
            'Inven Like List 데이터를 찾을 수 없습니다.'
          );
        }

        // ⚠️ Resolver와 동일하게 factory 적용
        // userInfoToken은 서버 사이드 context이므로 클라이언트에서는 null 전달
        // GraphQL 응답 형태로 래핑
        return {
          invenLikeList: createInvenList({
            invenListResult: response.result,
            type: InvenTypeVo.Like,
            userInfo: null,
          }),
        };
      },
      ...options,
    }),

  /**
   * 유료 구매 목록 조회 (Infinite)
   *
   * ⚠️ Resolver 로직:
   * - response.result 없으면 에러 throw
   * - createInvenList factory로 변환 (InvenTypeVo.Paid, userInfo 전달)
   *
   * ⚠️ Adapter에서 initialPageParam과 getNextPageParam을 반드시 전달해야 함
   */
  infiniteInvenPaidList: (
    variables: InvenPaidListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        InvenPaidListQuery, // TQueryFnData - GraphQL 응답 타입으로 변경
        PagewebError,
        InfiniteData<InvenPaidListQuery, InvenPaidListQueryVariables>,
        InvenPaidListQuery, // TResult
        ReturnType<typeof INVEN_QUERY_KEY_MAP.infiniteInvenPaidList>,
        InvenPaidListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: INVEN_QUERY_KEY_MAP.infiniteInvenPaidList(variables),
      queryFn: async ({pageParam}) => {
        const {categoryUid, sortType, size} = variables.invenListInput;

        const response = await getInvenPaid(
          {
            category_uid: categoryUid ?? undefined,
            sort_type: sortType ?? undefined,
            page: pageParam?.invenListInput?.page ?? undefined,
            size: size ?? undefined,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError(
            'Inven Paid List 데이터를 찾을 수 없습니다.'
          );
        }

        // ⚠️ Resolver와 동일하게 factory 적용
        // userInfoToken은 서버 사이드 context이므로 클라이언트에서는 null 전달
        return {
          invenPaidList: createInvenList({
            invenListResult: response.result,
            type: InvenTypeVo.Paid,
            userInfo: null,
          }),
        };
      },
      ...options,
    }),

  /**
   * 최근 본 목록 조회 (Infinite)
   *
   * ⚠️ Resolver 로직:
   * - response.result 없으면 에러 throw
   * - createInvenList factory로 변환 (InvenTypeVo.Recent, userInfo 전달)
   *
   * ⚠️ Adapter에서 initialPageParam과 getNextPageParam을 반드시 전달해야 함
   */
  infiniteInvenRecentList: (
    variables: InvenRecentListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        InvenRecentListQuery, // TQueryFnData - GraphQL 응답 타입으로 변경
        PagewebError,
        InfiniteData<InvenRecentListQuery, InvenRecentListQueryVariables>,
        InvenRecentListQuery, // TResult
        ReturnType<typeof INVEN_QUERY_KEY_MAP.infiniteInvenRecentList>,
        InvenRecentListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: INVEN_QUERY_KEY_MAP.infiniteInvenRecentList(variables),
      queryFn: async ({pageParam}) => {
        const {categoryUid, sortType, size} = variables.invenListInput;

        const response = await getInvenRecent(
          {
            category_uid: categoryUid ?? undefined,
            sort_type: sortType ?? undefined,
            page: pageParam?.invenListInput?.page ?? undefined,
            size: size ?? undefined,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError(
            'Inven Recent List 데이터를 찾을 수 없습니다.'
          );
        }

        // ⚠️ Resolver와 동일하게 factory 적용
        // userInfoToken은 서버 사이드 context이므로 클라이언트에서는 null 전달
        return {
          invenRecentList: createInvenList({
            invenListResult: response.result,
            type: InvenTypeVo.Recent,
            userInfo: null,
          }),
        };
      },
      ...options,
    }),

  /**
   * 인벤 검색 목록 조회 (Infinite)
   *
   * ⚠️ Resolver 로직:
   * - word, type, sortType, page, categoryUid 파라미터
   * - response.result 없으면 에러 throw
   * - createInvenList factory로 변환 (type을 InvenTypeVo로 캐스팅, userInfo 전달)
   *
   * ⚠️ Adapter에서 initialPageParam과 getNextPageParam을 반드시 전달해야 함
   */
  infiniteSearchInvenList: (
    variables: SearchInvenListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        SearchInvenListQuery, // TQueryFnData - GraphQL 응답 타입으로 변경
        PagewebError,
        InfiniteData<SearchInvenListQuery, SearchInvenListQueryVariables>,
        SearchInvenListQuery, // TResult
        ReturnType<typeof INVEN_QUERY_KEY_MAP.infiniteSearchInvenList>,
        SearchInvenListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: INVEN_QUERY_KEY_MAP.infiniteSearchInvenList(variables),
      queryFn: async ({pageParam}) => {
        const {word, type, sortType, categoryUid} =
          variables.searchInvenListInput;

        const response = await postInvenSearch(
          {
            word,
            type,
            sort_type: sortType,
            category_uid: categoryUid,
            page: pageParam?.searchInvenListInput?.page ?? undefined,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError(
            'Inven 검색 데이터를 찾을 수 없습니다.'
          );
        }

        // ⚠️ Resolver와 동일하게 factory 적용
        // userInfoToken은 서버 사이드 context이므로 클라이언트에서는 null 전달
        return {
          searchInvenList: createInvenList({
            invenListResult: response.result,
            type: type as InvenTypeVo,
            userInfo: null,
          }),
        };
      },
      ...options,
    }),

  /**
   * 내 인벤 정보 조회
   *
   * ⚠️ Resolver 로직:
   * - response.result 없으면 에러 throw
   * - createInvenMyInfo factory로 변환 (adapter에서 처리)
   */
  invenMyInfo: (
    variables?: InvenMyInfoQueryVariables,
    options?: Omit<
      UseQueryOptions<InvenMyInfo, PagewebError, InvenMyInfo>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: INVEN_QUERY_KEY_MAP.invenMyInfo(variables),
      queryFn: async () => {
        const response = await getInvenMyInfo(fetcherOptions);

        if (!response.result) {
          throw createSimpleApiResponseError(
            'Inven Myinfo 데이터를 찾을 수 없습니다.'
          );
        }

        const data = response.result;

        return createInvenMyInfo(data);
      },
      ...options,
    }),

  /**
   * 추천 목록 조회
   *
   * ⚠️ Resolver 로직:
   * - type을 InvenTypeVo로 전달
   * - response.result 없으면 에러 throw
   * - createInvenRecommedList factory로 변환 (userInfo 전달)
   */
  invenRecommendList: (
    variables: InvenRecommendListQueryVariables,
    options?: Omit<
      UseQueryOptions<
        {
          invenRecommendList: InvenRecommendList | undefined;
        },
        PagewebError,
        {invenRecommendList: InvenRecommendList | undefined}
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: INVEN_QUERY_KEY_MAP.invenRecommendList(variables),
      queryFn: async () => {
        const response = await getInvenRecommend(
          variables.type as InvenTypeVo,
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError(
            'Inven Recent List 데이터를 찾을 수 없습니다.'
          );
        }

        return {
          invenRecommendList: createInvenRecommedList(null, response.result),
        };
      },
      ...options,
    }),
} as const;
